import React, { ChangeEvent, useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import Select, { SingleValue } from 'react-select';
import {
  Box,
  Breadcrumbs,
  Link,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { ChevronRight } from 'lucide-react';
import { toast } from '../../../ui/toast';
import { PaginationController } from '../../../ui/pagination_controller/pagination_controller';
import { getJobList } from '../../../utils/api_service/job_api';
import { Permission } from '../../../utils/helpers/permissions';
import PermissionGate from '../../../utils/hooks/PermissionGate';
import {
  ContextBasedGeneratorJob,
  TopicBasedGeneratorJob,
} from '../../../utils/types/content_manager_types';
import { SelectOptions } from '../../../utils/types/react_select_types';
import { DocumentTriage } from './document_triage/document_triage';
import { ContextSearch } from './search_bar';

export const ContentManager = () => {
  const [jobList, setJobList] = useState<
    (ContextBasedGeneratorJob | TopicBasedGeneratorJob)[]
  >([]);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [loading, setLoading] = useState<boolean>(false);

  const generateTypeList: SelectOptions[] = [
    {
      value: 'context',
      label: 'Context',
    },
    {
      value: 'topic',
      label: 'Topic',
    },
  ];

  const [generateType, setGenerateType] = useState<SelectOptions | null>(null);

  useEffect(() => {
    setPageNumber(1);
  }, [searchTerm, generateType]);

  useEffect(() => {
    setLoading(true);
    const debounceSearchTerm = setTimeout(() => {
      const fetchJobList = async () => {
        try {
          let generateTypeValue = null;
          if (generateType?.value !== 'None') {
            generateTypeValue = generateType?.value;
          }
          const response = await getJobList(
            pageNumber,
            10,
            searchTerm,
            generateTypeValue,
          );
          const totalPagesCount = Math.ceil(response.totalJobs / 10);
          setJobList(response.jobs);
          setTotalPages(totalPagesCount);
        } catch (error) {
          toast.error('Error Fetching Jobs ' + error, {
            position: 'bottom-center',
          });
          setJobList([]);
          setLoading(false);
        } finally {
          setLoading(false);
        }
      };
      fetchJobList();
    }, 1000);

    return () => clearTimeout(debounceSearchTerm);
  }, [searchTerm, generateType, pageNumber]);

  const handleSearchTermChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const handleSelectChange = (selectedValue: SingleValue<SelectOptions>) => {
    setGenerateType(selectedValue);
  };

  return (
    <Stack spacing={2}>
      <Paper
        variant='outlined'
        sx={{
          p: 1.5,
          borderRadius: 2,
          display: 'flex',
          flexDirection: { xs: 'column', lg: 'row' },
          alignItems: { xs: 'flex-start', lg: 'center' },
          justifyContent: 'space-between',
          gap: 1.5,
          borderColor: 'divider',
        }}
      >
        <Breadcrumbs
          separator={<ChevronRight size={14} color='#94a3b8' />}
          aria-label='breadcrumb'
        >
          <Link
            component={RouterLink}
            to='#'
            underline='hover'
            color='text.secondary'
            fontSize='0.875rem'
          >
            Questions
          </Link>
          <Typography color='primary.main' fontWeight={600} fontSize='0.875rem'>
            Content Manager
          </Typography>
        </Breadcrumbs>

        <Stack direction='row' spacing={1} flexWrap='wrap'>
          <PermissionGate
            requiredPermissions={[Permission.QUESTIONS_VIEW]}
            matchAnyPermission={false}
          >
            <Link
              component={RouterLink}
              to='/question-bank'
              underline='none'
              sx={{
                px: 1.5,
                py: 0.5,
                borderRadius: 1,
                fontSize: '0.8125rem',
                fontWeight: 600,
                color: 'primary.main',
                '&:hover': { bgcolor: 'action.hover' },
              }}
            >
              Question Bank
            </Link>
          </PermissionGate>
          <PermissionGate
            requiredPermissions={[Permission.QUESTIONS_VIEW]}
            matchAnyPermission={false}
          >
            <Link
              component={RouterLink}
              to='/pre-screen-bank'
              underline='none'
              sx={{
                px: 1.5,
                py: 0.5,
                borderRadius: 1,
                fontSize: '0.8125rem',
                fontWeight: 600,
                color: 'primary.main',
                '&:hover': { bgcolor: 'action.hover' },
              }}
            >
              Pre Screen Question Bank
            </Link>
          </PermissionGate>
          <PermissionGate
            requiredPermissions={[Permission.JOB_CREATE]}
            matchAnyPermission={false}
          >
            <Link
              component={RouterLink}
              to='/upload-file'
              underline='none'
              sx={{
                px: 1.5,
                py: 0.5,
                borderRadius: 1,
                fontSize: '0.8125rem',
                fontWeight: 600,
                color: 'primary.main',
                '&:hover': { bgcolor: 'action.hover' },
              }}
            >
              Auto Generate Questions
            </Link>
          </PermissionGate>
        </Stack>
      </Paper>

      <Stack spacing={2}>
        <Paper
          variant='outlined'
          sx={{
            p: 1.5,
            borderRadius: 2,
            borderColor: 'divider',
          }}
        >
          <Stack
            direction={{ xs: 'column', lg: 'row' }}
            spacing={2}
            alignItems={{ lg: 'center' }}
          >
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <ContextSearch
                searchTerm={searchTerm}
                handleChange={handleSearchTermChange}
              />
            </Box>
            <Box sx={{ width: { xs: '100%', lg: 288 } }}>
              <Select
                placeholder='Select Generation Type'
                name='generationType'
                options={generateTypeList}
                onChange={handleSelectChange}
                isClearable={true}
                classNamePrefix='content-filter'
              />
            </Box>
          </Stack>
        </Paper>

        <DocumentTriage
          loading={loading}
          documents={jobList}
          setDocuments={setJobList}
        />

        <Paper
          variant='outlined'
          sx={{
            px: 2,
            py: 1.5,
            borderRadius: 2,
            borderColor: 'divider',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Typography variant='caption' fontWeight={600} color='text.secondary'>
            Page {pageNumber} of {totalPages || 1}
          </Typography>
          <PaginationController
            pageNumber={pageNumber}
            maxPageNumber={totalPages}
            navigateNextPage={() => setPageNumber((prev) => prev + 1)}
            navigatePreviousPage={() => setPageNumber((prev) => prev - 1)}
          />
        </Paper>
      </Stack>
    </Stack>
  );
};
